import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LISTING_STATUS, getListingStatusSeverity } from '../utils/statusBadges';

const SaleListingsTable = ({
    data = [],
    totalRecords = 0,
    loading = false,
    pageNumber = 1,
    pageSize = 20,
    onPageChange,
    onViewDetail,
    onCreatePurchaseRequest,
    onCancelMyRequest
}) => {
    const { th, getCurrentLocale } = useLanguage();

    const formatDate = (value) => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleDateString(getCurrentLocale(), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return value;
        }
    };

    const statusBody = (row) => (
        <Tag
            value={row.statusText}
            severity={getListingStatusSeverity(row.status)}
        />
    );

    const sellerBody = (row) => {
        return (
            <div className="flex flex-column">
                <span>{row.sellerBranchName || '-'}</span>
                {row.isOwnListing && (
                    <Tag
                        className="mt-1 w-max"
                        value={th('vehiclesForSale.badge.ownListing')}
                        severity="info"
                        icon="pi pi-user"
                    />
                )}
            </div>
        );
    };

    const actionBody = (row) => {
        if (row.isOwnListing) {
            return (
                <div className="flex align-items-center gap-2">
                    <Button
                        icon="pi pi-eye"
                        rounded
                        text
                        severity="info"
                        tooltip={th('vehiclesForSale.buttons.viewDetail')}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => onViewDetail?.(row)}
                    />
                    {row.pendingRequestCount > 0 && (
                        <Badge
                            value={row.pendingRequestCount}
                            severity="warning"
                            title={th('vehiclesForSale.columns.pendingRequests')}
                        />
                    )}
                </div>
            );
        }

        if (row.hasMyPendingRequest) {
            return (
                <div className="flex align-items-center gap-2">
                    <Tag
                        value={th('vehiclesForSale.badge.myRequest')}
                        severity="warning"
                        icon="pi pi-send"
                    />
                    <Button
                        icon="pi pi-times"
                        rounded
                        text
                        severity="danger"
                        tooltip={th('vehiclesForSale.buttons.cancelMyRequest')}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => onCancelMyRequest?.(row)}
                    />
                </div>
            );
        }

        if (row.status === LISTING_STATUS.ACTIVE) {
            return (
                <Button
                    label={th('vehiclesForSale.buttons.createPurchaseRequest')}
                    icon="pi pi-shopping-cart"
                    size="small"
                    onClick={() => onCreatePurchaseRequest?.(row)}
                />
            );
        }

        return <span className="text-500">—</span>;
    };

    const handlePageEvent = (e) => {
        const newPageNumber = Math.floor(e.first / e.rows) + 1;
        onPageChange?.({ pageNumber: newPageNumber, pageSize: e.rows });
    };

    return (
        <DataTable
            value={data}
            paginator
            lazy
            first={(pageNumber - 1) * pageSize}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageEvent}
            rowsPerPageOptions={[10, 20, 50]}
            loading={loading}
            showGridlines
            stripedRows
            responsiveLayout="scroll"
            emptyMessage={th('vehiclesForSale.emptyMessages.noListings')}
            dataKey="listingId"
            className="p-datatable-gridlines"
        >
            <Column
                header={th('vehiclesForSale.columns.actions')}
                body={actionBody}
                style={{ minWidth: '200px', width: '220px' }}
                frozen
            />
            <Column field="chassisNo" header={th('vehiclesForSale.columns.chassisNo')} sortable style={{ minWidth: '150px' }} />
            <Column field="brand" header={th('vehiclesForSale.columns.brand')} sortable style={{ minWidth: '100px' }} />
            <Column field="model" header={th('vehiclesForSale.columns.model')} sortable style={{ minWidth: '100px' }} />
            <Column
                field="sellerBranchName"
                header={th('vehiclesForSale.columns.sellerDealer')}
                body={sellerBody}
                sortable
                style={{ minWidth: '200px' }}
            />
            <Column
                field="createdDate"
                header={th('vehiclesForSale.columns.listingDate')}
                body={(row) => formatDate(row.createdDate)}
                sortable
                style={{ minWidth: '140px' }}
            />
            <Column
                field="statusText"
                header={th('vehiclesForSale.columns.status')}
                body={statusBody}
                sortable
                style={{ minWidth: '120px' }}
            />
        </DataTable>
    );
};

export default SaleListingsTable;
